import { BaseScrollGlobalStyle } from '@toggle/design-system';
import React, { FC, Suspense, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Outlet, useLocation } from 'react-router';

import { Loader } from '~/components/loader/Loader';
import { ToastsLayout } from '~/components/toasts-layout/ToastsLayout';
import { appPaths, authPaths } from '~/routes/app-paths';
import { getSession, useAuth } from '~/stores/use-auth/useAuth';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useLivePriceWebSocketStore } from '~/stores/use-live-price-websocket-store/useLivePriceWebSocketStore';

import { AppMetadata } from './components';
import { AppHistory } from './components/app-history/AppHistory';
import { Maintenance } from './components/maintenance/Maintenance';
import { MaintenanceBanner } from './components/maintenance-banner/MaintenanceBanner';
import { NotificationAlerts } from './components/notification-alerts/NotificationAlerts';
import { useTouchDevice } from './hooks/MediaQueryHook';
import { useNotificationsListener } from './hooks/use-notifications-listener/useNotificationsListener';
import * as S from './Root.styles';
import { useNavigator } from './stores/use-navigator/useNavigator';
import { useChatNavigation } from './views/alfred/use-chat-navigation/useChatNavigation';
import { MobileNotSupported } from './views/auth/mobile-not-supported/MobileNotSupported';

export const mobileAlfredPaths = [authPaths.login, appPaths.alfred];

export const Root: FC = () => {
  const { authProcessing, initStores, setAuthProcessing } = useAuth();
  const { listenToNavigator } = useNavigator();

  const location = useLocation();
  const isTouchDevice = useTouchDevice();
  const { mode } = useChatNavigation();

  const isIbRoute = location.pathname.includes(appPaths.ib);

  const isLoginPath = location.pathname === authPaths.login;
  const isAlfredPath = location.pathname === appPaths.alfred;
  const enableMobileReasoner =
    isLoginPath || (isAlfredPath && mode === 'research');

  const isUnsupportedScreenSize =
    isTouchDevice && !isIbRoute && !enableMobileReasoner;
  const { checkIsInMaintenance, getSystemFlags } = useFlags(state => ({
    checkIsInMaintenance: state.checkIsInMaintenance,
    getSystemFlags: state.initialise,
  }));
  useNotificationsListener();

  const initSession = async () => {
    const session = await getSession();
    await getSystemFlags();

    if (session) {
      await initStores(session);
    }

    setAuthProcessing(false);
  };

  useEffect(() => {
    initSession();

    return () => {
      useLivePriceWebSocketStore.getState().closeConnection();
    };
  }, []);

  useEffect(() => {
    listenToNavigator();
  }, []);

  if (checkIsInMaintenance()) {
    return (
      <>
        <Suspense fallback={<Loader />}>
          <Maintenance />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <AppMetadata path={location.pathname} />
      <Suspense fallback={<Loader />}>
        {authProcessing ? (
          <Loader />
        ) : (
          <>
            <BaseScrollGlobalStyle />
            <MaintenanceBanner />
            <NotificationAlerts />
            <DndProvider backend={HTML5Backend}>
              <AppHistory>
                <S.StyledGlobalPortals
                  $isUnsupportedScreenSize={isUnsupportedScreenSize}
                />
                <S.StyledMobileNotSupportedWrapper
                  $isUnsupportedScreenSize={isUnsupportedScreenSize}
                >
                  <MobileNotSupported />
                </S.StyledMobileNotSupportedWrapper>
                <S.StyledOutletWrapper
                  $isUnsupportedScreenSize={isUnsupportedScreenSize}
                >
                  <Outlet />
                </S.StyledOutletWrapper>
              </AppHistory>
            </DndProvider>
          </>
        )}
      </Suspense>
      <ToastsLayout />
    </>
  );
};
